import React, { useState, useMemo } from 'react';
import type { CalendarDay, LeaveRequest, LeaveType } from '../../redux/types/attendanceTypes';

// ── Constants ─────────────────────────────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  PRESENT: 'bg-green-100 text-green-800',
  ABSENT: 'bg-red-100 text-red-800',
  HALF_DAY: 'bg-yellow-100 text-yellow-800',
  ON_LEAVE: 'bg-blue-100 text-blue-800',
  WEEKEND: 'bg-gray-100 text-gray-400',
  UPCOMING: 'bg-white text-gray-300',
};

const CELL_BG: Record<string, string> = {
  PRESENT: 'bg-[#43DF65] text-white',
  ABSENT: 'bg-[#FF5959] text-white',
  HALF_DAY: 'bg-[#7EC6EA] text-white',
  ON_LEAVE: 'bg-[#FF5959] text-white',
  WEEKEND: 'bg-gray-100 text-gray-400',
};

const CELL_LABEL: Record<string, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  HALF_DAY: 'Half Day',
  ON_LEAVE: 'On Leave',
  WEEKEND: 'WE',
};

export const STATUS_DOT: Record<string, string> = {
  PRESENT: 'bg-[#43DF65]',
  ABSENT: 'bg-[#FF5959]',
  HALF_DAY: 'bg-[#7EC6EA]',
  ON_LEAVE: 'bg-[#FF5959]',
  WEEKEND: 'bg-gray-300',
};

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const formatTime = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// ── Calendar ──────────────────────────────────────────────────────────────────

export const AttendanceCalendar: React.FC<{
  days: CalendarDay[];
  year: number;
  month: number;
}> = ({ days, year, month }) => {
  // Monday-start week (Mon=0 … Sun=6)
  let firstDay = new Date(year, month - 1, 1).getDay();
  firstDay = firstDay === 0 ? 6 : firstDay - 1;
  const totalDays = new Date(year, month, 0).getDate();
  const trailingBlanks = (7 - ((firstDay + totalDays) % 7)) % 7;

  const dayMap = useMemo(
    () => new Map(days.map(d => [new Date(d.date).getUTCDate(), d])),
    [days]
  );

  return (
    <div>
      {/* Week headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 py-2">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
          <div key={d} className="text-center text-xs font-semibold text-gray-500">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`b${i}`} className="border border-gray-100 min-h-[56px] sm:min-h-[88px]" />
        ))}

        {Array.from({ length: totalDays }, (_, i) => {
          const date = i + 1;
          const day = dayMap.get(date);
          const isToday =
            date === new Date().getDate() &&
            month === new Date().getMonth() + 1 &&
            year === new Date().getFullYear();

          return (
            <div
              key={date}
              className={`border border-gray-100 min-h-[56px] sm:min-h-[88px] p-1 sm:p-2 flex flex-col justify-between ${isToday ? 'bg-blue-50' : ''}`}
              title={
                day
                  ? [
                      CELL_LABEL[day.status] ?? day.status,
                      day.checkInTime ? `In: ${formatTime(day.checkInTime)}` : '',
                      day.checkOutTime ? `Out: ${formatTime(day.checkOutTime)}` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : ''
              }
            >
              <p
                className={`text-[10px] sm:text-sm text-right font-medium ${
                  isToday ? 'text-blue-600' : 'text-gray-700'
                }`}
              >
                {date}
              </p>

              {day && day.status !== 'UPCOMING' && day.status !== 'WEEKEND' && (
                <span
                  className={`text-[8px] sm:text-[11px] px-0.5 sm:px-1 py-0.5 rounded-md w-full text-center font-medium leading-tight ${CELL_BG[day.status] ?? ''}`}
                >
                  {day.workingHours !== null
                    ? `${day.workingHours.toFixed(1)}h`
                    : (CELL_LABEL[day.status] ?? day.status)}
                </span>
              )}

              {day && day.status === 'WEEKEND' && (
                <span className="text-[8px] sm:text-[10px] text-gray-400 text-center">WE</span>
              )}
            </div>
          );
        })}

        {Array.from({ length: trailingBlanks }).map((_, i) => (
          <div key={`e${i}`} className="border border-gray-100 min-h-[56px] sm:min-h-[88px]" />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4">
        {[
          { label: 'Present', cls: 'bg-[#43DF65]' },
          { label: 'Half Day', cls: 'bg-[#7EC6EA]' },
          { label: 'On Leave / Absent', cls: 'bg-[#FF5959]' },
          { label: 'Weekend', cls: 'bg-gray-300' },
        ].map(({ label, cls }) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cls}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Leave Request Form ────────────────────────────────────────────────────────

export const LeaveRequestForm: React.FC<{
  leaveTypes: LeaveType[];
  onSubmit: (data: { leaveTypeId: string; startDate: string; endDate: string; reason: string }) => void;
  loading: boolean;
}> = ({ leaveTypes, onSubmit, loading }) => {
  const [form, setForm] = useState({ leaveTypeId: '', startDate: '', endDate: '', reason: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.leaveTypeId || !form.startDate || !form.endDate || !form.reason) return;
    if (new Date(form.startDate) > new Date(form.endDate)) return;
    onSubmit(form);
    setForm({ leaveTypeId: '', startDate: '', endDate: '', reason: '' });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100"
    >
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Leave Type</label>
        <select
          value={form.leaveTypeId}
          onChange={e => setForm(f => ({ ...f, leaveTypeId: e.target.value }))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          <option value="">Select type</option>
          {leaveTypes.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
        <input
          type="date"
          value={form.startDate}
          onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
        <input
          type="date"
          value={form.endDate}
          onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
        <textarea
          value={form.reason}
          onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
        />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={loading || !form.leaveTypeId || !form.startDate || !form.endDate || !form.reason}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-5 py-2 rounded-lg font-medium transition-colors"
        >
          {loading ? 'Submitting...' : 'Submit Leave Request'}
        </button>
      </div>
    </form>
  );
};

// ── Leave Requests Table / Cards ──────────────────────────────────────────────

export const LeaveRequestsTable: React.FC<{
  requests: LeaveRequest[];
  showEmployee?: boolean;
  onReview?: (id: string, status: 'APPROVED' | 'REJECTED', note: string) => void;
  reviewLoading?: boolean;
}> = ({ requests, showEmployee, onReview, reviewLoading }) => {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [localStatus, setLocalStatus] = useState<Record<string, 'APPROVED' | 'REJECTED'>>({});

  if (!requests.length) {
    return <p className="text-sm text-gray-400 py-4 text-center">No leave requests found.</p>;
  }

  // Manager mode — card list per pending request, table for reviewed
  if (onReview) {
    const pending = requests.filter(r => r.status === 'PENDING' && !localStatus[r.id]);
    const actioned = requests.filter(r => r.status !== 'PENDING' || localStatus[r.id]);

    return (
      <div className="flex flex-col gap-4">
        {pending.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-3">No pending requests.</p>
        )}

        {pending.map(r => {
          const dayCount = Math.max(
            1,
            Math.round((new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86_400_000) + 1
          );
          const note = notes[r.id] ?? '';

          return (
            <div
              key={r.id}
              className="border border-gray-100 rounded-2xl p-4 bg-gray-50 hover:bg-white transition-colors shadow-sm"
            >
              {/* Employee info */}
              {showEmployee && (
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0 uppercase">
                    {r.employee?.fullname?.[0] ?? '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-800 truncate">{r.employee?.fullname ?? '—'}</p>
                    {r.employee?.employeeId && (
                      <p className="text-xs text-gray-400">#{r.employee.employeeId}</p>
                    )}
                  </div>
                  <span
                    className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0 ${
                      (r.leaveType?.name ?? '').toLowerCase().includes('half') || !r.leaveType
                        ? 'bg-[#F7F7FF] text-[#0698AD]'
                        : 'bg-[#FFF9EA] text-[#B98500]'
                    }`}
                  >
                    {r.leaveType?.name ?? 'Half Day'}
                  </span>
                </div>
              )}

              {/* Type + day count for own requests */}
              {!showEmployee && (
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-sm text-gray-800">{r.leaveType?.name ?? 'Half Day'}</span>
                  <span className="text-xs text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                    {dayCount} day{dayCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Dates */}
              <div className="flex items-start gap-2 text-xs text-gray-600 mb-3">
                <div>
                  <p className="text-gray-400">From</p>
                  <p className="font-bold text-gray-800">{formatDate(r.startDate)}</p>
                </div>
                <span className="flex-1 text-center text-gray-300 mt-3">──────</span>
                <div className="text-right">
                  <p className="text-gray-400">To</p>
                  <p className="font-bold text-gray-800">{formatDate(r.endDate)}</p>
                </div>
                {showEmployee && (
                  <span className="ml-1 mt-2 text-xs text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap self-start flex-shrink-0">
                    {dayCount}d
                  </span>
                )}
              </div>

              {/* Reason */}
              {r.reason && (
                <p className="text-xs text-gray-500 mb-3 line-clamp-2 italic">"{r.reason}"</p>
              )}

              {/* Review note */}
              <input
                type="text"
                placeholder="Review note (optional)"
                value={note}
                onChange={e => setNotes(prev => ({ ...prev, [r.id]: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs mb-3 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
              />

              {/* Action buttons */}
              <div className="flex gap-2">
                <div className="relative flex-1 h-10 overflow-hidden rounded-lg">
                  <button
                    onClick={() => {
                      setLocalStatus(prev => ({ ...prev, [r.id]: 'REJECTED' }));
                      onReview(r.id, 'REJECTED', note);
                    }}
                    disabled={reviewLoading}
                    className="absolute inset-0 z-10 bg-[#F65244] hover:bg-[#e04335] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Reject
                  </button>
                </div>
                <div className="relative flex-1 h-10 overflow-hidden rounded-lg">
                  <button
                    onClick={() => {
                      setLocalStatus(prev => ({ ...prev, [r.id]: 'APPROVED' }));
                      onReview(r.id, 'APPROVED', note);
                    }}
                    disabled={reviewLoading}
                    className="absolute inset-0 z-10 bg-[#28A745] hover:bg-[#218838] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Reviewed requests as compact table */}
        {actioned.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2 mt-2">
              Past Requests
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    {showEmployee && <th className="py-2 pr-4 font-medium">Employee</th>}
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium">From</th>
                    <th className="py-2 pr-4 font-medium">To</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {actioned.map(r => {
                    const effectiveStatus = localStatus[r.id] ?? r.status;
                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        {showEmployee && (
                          <td className="py-2 pr-4 font-medium text-gray-800">
                            {r.employee?.fullname ?? '—'}
                          </td>
                        )}
                        <td className="py-2 pr-4 text-gray-700">{r.leaveType?.name ?? 'Half Day'}</td>
                        <td className="py-2 pr-4 text-gray-600">{formatDate(r.startDate)}</td>
                        <td className="py-2 pr-4 text-gray-600">{formatDate(r.endDate)}</td>
                        <td className="py-2">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              effectiveStatus === 'APPROVED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {effectiveStatus}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Read-only table (employee own history / admin view)
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
            {showEmployee && <th className="py-2 pr-4 font-medium">Employee</th>}
            <th className="py-2 pr-4 font-medium">Type</th>
            <th className="py-2 pr-4 font-medium">From</th>
            <th className="py-2 pr-4 font-medium">To</th>
            <th className="py-2 pr-4 font-medium">Reason</th>
            <th className="py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {requests.map(r => (
            <tr key={r.id} className="hover:bg-gray-50">
              {showEmployee && (
                <td className="py-2 pr-4 font-medium text-gray-800">
                  {r.employee?.fullname ?? '—'}
                </td>
              )}
              <td className="py-2 pr-4 text-gray-700">{r.leaveType?.name ?? 'Half Day'}</td>
              <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{formatDate(r.startDate)}</td>
              <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{formatDate(r.endDate)}</td>
              <td
                className="py-2 pr-4 text-gray-600 max-w-[180px] truncate"
                title={r.reason}
              >
                {r.reason}
              </td>
              <td className="py-2">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.status === 'APPROVED'
                      ? 'bg-green-100 text-green-700'
                      : r.status === 'REJECTED'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
